import * as React from 'react'
import { observer } from 'mobx-react'
import Icon from 'components/Icon'
import { ContentLayout, ContentMain } from 'components/ContentLayout'
import PayoutsAdminHeader from 'payouts/admin/components/PayoutsAdminHeader'
import { Sandwich, SandwichBun, SandwichContent } from 'components/Sandwich'
import ListHeader from 'components/ListHeader'
import ContentError from 'components/ContentError'
import {
  SideSheet,
  SideSheetHostView,
  SideSheetMain,
} from 'components/SideSheet'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import Button from 'components/Button'
import NewUserTransactionModal from '../NewUserTransactionModal'
import { PayoutReviewViewModel } from '../../PayoutReviewViewModel'
import PendingTransactionsTable from './PendingTransactionsTable'
import { useActivation } from 'hooks/mobx-hooks'
import CancelUserTransactionModal from '../CancelUserTransactionModal'
import DraftingView from './DraftingView'

interface PayoutDraftingViewProps {
  vm: PayoutReviewViewModel
}

/**
 * The PayoutReview screen.
 */
export default observer(function PayoutReview({ vm }: PayoutDraftingViewProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  useActivation(vm)

  if (!vm.canAccessPayoutsAdministration) {
    return (
      <ContentError
        title="Denied"
        large
        error="You are not allowed to access this page."
      />
    )
  }

  return (
    <>
      <SideSheetHostView
        sheetVisible={vm.draftingVM.approvedTransactions.length > 0}
      >
        <SideSheetMain>
          <Sandwich stretch>
            <SandwichBun>
              <PayoutsAdminHeader scrollOffset={scrollOffset}>
                <Button
                  type="secondary"
                  disabled={vm.actionsDisabled}
                  iconLeft={<Icon auto name="add" />}
                  onClick={vm.newUserTransactionVM.show}
                >
                  New Transaction
                </Button>
              </PayoutsAdminHeader>
            </SandwichBun>
            <SandwichContent stretch>
              <ContentLayout
                accountForLabel
                accountForTabBar
                fill
                ref={scrollRef}
              >
                <ContentMain fill constrainWidth>
                  <Sandwich stretch>
                    <SandwichBun>
                      <ListHeader
                        count={vm.pendingTransactionsCount || undefined}
                      >
                        Pending transactions
                      </ListHeader>
                    </SandwichBun>
                    <SandwichContent stretch>
                      <PendingTransactionsTable vm={vm} />
                    </SandwichContent>
                  </Sandwich>
                </ContentMain>
              </ContentLayout>
            </SandwichContent>
          </Sandwich>
        </SideSheetMain>

        <SideSheet>
          <DraftingView vm={vm.draftingVM} />
        </SideSheet>
      </SideSheetHostView>
      <NewUserTransactionModal vm={vm.newUserTransactionVM} />
      {vm.cancelUserTransactionVM && (
        <CancelUserTransactionModal vm={vm.cancelUserTransactionVM} />
      )}
    </>
  )
})
